// Custom hook for diary data with React Query caching
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import diaryService from '../services/diaryService';

// Query keys for caching
export const DIARY_QUERY_KEYS = {
  readings: (page, limit, filters) => ['diary-readings', page, limit, filters],
  stats: () => ['diary-stats'],
  reading: (id) => ['diary-reading', id],
  search: (query, filters) => ['diary-search', query, filters],
};

// Hook for fetching paginated readings with caching
export const useDiaryReadings = (page = 1, limit = 20, filters = {}) => {
  return useQuery({
    queryKey: DIARY_QUERY_KEYS.readings(page, limit, filters),
    queryFn: () => diaryService.getUserReadings(page, limit, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true, // Keep previous page data while loading new page
    retry: 1,
  });
};

// Hook for fetching diary statistics
export const useDiaryStats = () => {
  return useQuery({
    queryKey: DIARY_QUERY_KEYS.stats(),
    queryFn: () => diaryService.getReadingStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes - stats change less frequently
    cacheTime: 15 * 60 * 1000, // 15 minutes
    retry: 1,
  });
};

// Hook for fetching a single reading
export const useDiaryReading = (id) => {
  return useQuery({
    queryKey: DIARY_QUERY_KEYS.reading(id),
    queryFn: () => diaryService.getReading(id),
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
    enabled: !!id, // Only run if id exists
    retry: 1,
  });
};

// Hook for searching readings
export const useSearchReadings = (searchQuery, filters = {}) => {
  return useQuery({
    queryKey: DIARY_QUERY_KEYS.search(searchQuery, filters),
    queryFn: () => diaryService.searchReadings(searchQuery, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes - search results change more often
    cacheTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!searchQuery && searchQuery.trim().length > 0, // Only search if query exists
    retry: 1,
  });
};

// Hook for diary mutations (create, update, delete)
export const useDiaryMutations = () => {
  const queryClient = useQueryClient();

  // Delete reading mutation
  const deleteReading = useMutation({
    mutationFn: (id) => diaryService.deleteReading(id),
    onSuccess: (_, deletedId) => {
      // Remove from all reading lists
      queryClient.setQueriesData(
        { queryKey: ['diary-readings'], exact: false },
        (oldData) => {
          if (!oldData?.readings) return oldData;
          return {
            ...oldData,
            readings: oldData.readings.filter(reading => reading.id !== deletedId),
            total: Math.max(0, (oldData.total || 0) - 1)
          };
        }
      );

      // Invalidate stats to refresh counts
      queryClient.invalidateQueries({ queryKey: DIARY_QUERY_KEYS.stats() });
      
      // Remove the specific reading from cache
      queryClient.removeQueries({ queryKey: DIARY_QUERY_KEYS.reading(deletedId) });
    },
    onError: (error) => {
      console.error('Delete reading error:', error);
    }
  });

  // Update reading mutation
  const updateReading = useMutation({
    mutationFn: ({ id, data }) => diaryService.updateReading(id, data),
    onSuccess: (updatedReading, { id }) => {
      // Update the specific reading in cache
      queryClient.setQueryData(DIARY_QUERY_KEYS.reading(id), updatedReading);
      
      // Update reading in all reading lists
      queryClient.setQueriesData(
        { queryKey: ['diary-readings'], exact: false },
        (oldData) => {
          if (!oldData?.readings) return oldData;
          return {
            ...oldData,
            readings: oldData.readings.map(reading => 
              reading.id === id ? updatedReading : reading
            )
          };
        }
      );

      // Invalidate stats in case update affects counts
      queryClient.invalidateQueries({ queryKey: DIARY_QUERY_KEYS.stats() });
    },
    onError: (error) => {
      console.error('Update reading error:', error);
    }
  });

  // Save new reading mutation
  const saveReading = useMutation({
    mutationFn: (readingData) => diaryService.saveReading(readingData),
    onSuccess: (result) => {
      console.log('Save reading mutation success:', result);
      // Extract the reading from the API response
      const newReading = result.reading || result;
      
      // Add to the beginning of all reading lists
      queryClient.setQueriesData(
        { queryKey: ['diary-readings'], exact: false },
        (oldData) => {
          if (!oldData?.readings) return oldData;
          return {
            ...oldData,
            readings: [newReading, ...oldData.readings],
            total: (oldData.total || 0) + 1
          };
        }
      );

      // Also invalidate all reading queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['diary-readings'] });
      queryClient.invalidateQueries({ queryKey: DIARY_QUERY_KEYS.stats() });
    },
    onError: (error) => {
      console.error('Save reading error:', error);
    }
  });

  return {
    deleteReading,
    updateReading,
    saveReading,
  };
};

// Utility function to invalidate all diary caches
export const useInvalidateDiary = () => {
  const queryClient = useQueryClient();
  
  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ['diary-readings'] });
      queryClient.invalidateQueries({ queryKey: ['diary-stats'] });
      queryClient.invalidateQueries({ queryKey: ['diary-search'] });
    },
    invalidateReadings: () => {
      queryClient.invalidateQueries({ queryKey: ['diary-readings'] });
    },
    invalidateStats: () => {
      queryClient.invalidateQueries({ queryKey: ['diary-stats'] });
    }
  };
};

// Utility function to prefetch data
export const usePrefetchDiary = () => {
  const queryClient = useQueryClient();

  return {
    prefetchReadings: (page = 1, limit = 20, filters = {}) => {
      queryClient.prefetchQuery({
        queryKey: DIARY_QUERY_KEYS.readings(page, limit, filters),
        queryFn: () => diaryService.getUserReadings(page, limit, filters),
        staleTime: 5 * 60 * 1000,
      });
    },
    prefetchStats: () => {
      queryClient.prefetchQuery({
        queryKey: DIARY_QUERY_KEYS.stats(),
        queryFn: () => diaryService.getReadingStats(),
        staleTime: 10 * 60 * 1000,
      });
    }
  };
};
